// Packages
import { Suspense, useContext, useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil'
import { useParams, useLocation } from 'react-router-dom'

// Store
import {
  countryId,
  cityId,
  cuisineId,
  countryData,
  countryProps,
  cityData,
  cuisineData,
  filterHalal,
  merchantsData,
} from '../../Store/Store'

// Context
import { Language } from '../../Language/Language'

// Helpers
import { buildSelect, resolveInitialCountryId } from './Filters.helpers'

// Elements
import {
  Container,
  Filter,
  Label,
  Select,
  InlineCheckbox,
  AccordionCheckboxRow,
  AccordionItem,
  AccordionHeader,
  AccordionChevron,
  AccordionPanel,
  RadioGroupHeading,
  RadioRow,
} from './Filters.elements'
import { IconChevronDown } from './Results.icons'

function FiltersSuspense({ filters }) {
  return (
    <>
      <Filter>
        <Label>{filters.country}</Label>
        <Select disabled />
      </Filter>
      <Filter>
        <Label>{filters.city}</Label>
        <Select disabled />
      </Filter>
      <Filter>
        <Label>{filters.cuisine}</Label>
        <Select disabled />
      </Filter>
    </>
  )
}

function AccordionShell({ label, children }) {
  const [open, setOpen] = useState(false)
  // Header reads cleaner without the trailing colon used by the <select> labels
  const heading = label.replace(/:\s*$/, '')

  return (
    <AccordionItem>
      <AccordionHeader
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{heading}</span>
        <AccordionChevron open={open}>
          <IconChevronDown />
        </AccordionChevron>
      </AccordionHeader>
      {open && <AccordionPanel>{children}</AccordionPanel>}
    </AccordionItem>
  )
}

function FilterStrings({
  name,
  label,
  all,
  atom,
  selector,
  asAccordion,
  labelExtra,
}) {
  const { locale } = useParams()

  const [selected, setSelected] = useRecoilState(atom)
  const items = useRecoilValue(selector)
  const props = useRecoilValue(countryProps)

  const market = locale.replace('-', '_')
  const hideSelect = (name === 'city' && !props.showCityDropdown) || false

  function translate(item) {
    const f = item.translations[market] || item.translations['en']
    return f.title
  }

  function selectValue(value) {
    const selectedItem = (items || []).find((item) => item.id === value)
    setSelected(value)
    window.tuiTrack.trackEvent(
      'click',
      `filterSelect:${name}:${selectedItem?.title || value}`,
    )
  }

  function onChangeHandler({ target: { value } }) {
    selectValue(value)
  }

  if (!items) return null

  if (asAccordion) {
    if (hideSelect) return null
    return (
      <AccordionShell label={label}>
        <RadioRow>
          <input
            type="radio"
            name={name}
            checked={!selected}
            onChange={() => selectValue('')}
          />
          <span>{all}</span>
        </RadioRow>
        {items.map((item) => (
          <RadioRow key={`${props.key}-${item.id}`}>
            <input
              type="radio"
              name={name}
              checked={selected === item.id}
              onChange={() => selectValue(item.id)}
            />
            <span>{translate(item)}</span>
          </RadioRow>
        ))}
      </AccordionShell>
    )
  }

  return buildSelect({
    name,
    label,
    all,
    items,
    countryId: props.key,
    onChangeHandler,
    hideSelect,
    market,
    labelExtra,
  })
}

function FilterCountry({
  label,
  showJapan,
  asAccordion,
  countryAtom = countryId,
  cityAtom = cityId,
  cuisineAtom = cuisineId,
}) {
  const { locale } = useParams()
  const { pathname } = useLocation()
  const isCenturion = pathname.includes('/centurion')

  const countries = useRecoilValue(countryData)
  const [id, setCountryId] = useRecoilState(countryAtom)
  const [hideCountrySelect, setHideCountrySelect] = useState(false)
  const props = useRecoilValue(countryProps)
  const resetCity = useResetRecoilState(cityAtom)
  const resetCuisine = useResetRecoilState(cuisineAtom)
  const localeCountry = locale.split('-')[1].toUpperCase()

  function selectCountry(value) {
    const selectedCountry = countries
      .flatMap((region) => region.countries)
      .find((country) => country.key === value)
    setCountryId(value)
    resetCity()
    resetCuisine()
    window.tuiTrack.trackEvent(
      'click',
      `filterSelect:country:${selectedCountry?.title || value}`,
    )
  }

  function onChangeHandler({ target }) {
    selectCountry(target.value)
  }

  function translate(item) {
    const market = locale.replace('-', '_')
    const f = item.translations[market] || item.translations['en']
    return f.title
  }

  function isVisibleCountry(country) {
    return (
      country.isActive &&
      (country.key === id || !hideCountrySelect) &&
      (country.isVisibleToOtherMarkets || country.key === localeCountry) &&
      !(locale === 'en-sg' && country.key === 'SG')
    )
  }

  useEffect(() => {
    // Only initialise the country once; don't clobber an existing selection
    // (the mobile filter sheet unmounts/remounts this component on each open).
    // Note: the primary initialisation happens in Listing so it runs even while
    // this component is unmounted inside the closed mobile sheet.
    if (id) return
    setCountryId(resolveInitialCountryId(countries, locale))
  }, [countries, id, locale, setCountryId])

  useEffect(() => {
    if (props.key && props.key === id) {
      setHideCountrySelect(
        !isCenturion && !props.canBrowseOtherMarkets && localeCountry === id,
      )
    }
  }, [props.key, props.canBrowseOtherMarkets, isCenturion, localeCountry, id])

  if (asAccordion) {
    if (hideCountrySelect || !id) return null
    return (
      <AccordionShell label={label}>
        {countries.map((region) => {
          const visible = region.countries.filter(isVisibleCountry)
          if (!visible.length) return null
          return (
            <div key={region.title}>
              <RadioGroupHeading>{translate(region)}</RadioGroupHeading>
              {visible.map((country) => (
                <RadioRow key={country.key}>
                  <input
                    type="radio"
                    name="country"
                    checked={id === country.key}
                    onChange={() => selectCountry(country.key)}
                  />
                  <span>{translate(country)}</span>
                </RadioRow>
              ))}
            </div>
          )
        })}
      </AccordionShell>
    )
  }

  return (
    <Filter style={{ display: hideCountrySelect ? 'none' : '' }} id="filters">
      <Label>{label}</Label>
      {id && (
        <Select
          name="country"
          id="country"
          value={id}
          onChange={onChangeHandler}
        >
          {countries.map((region) => (
            <optgroup key={region.title} label={translate(region)}>
              {region.countries.map(
                (country) =>
                  isVisibleCountry(country) && (
                    <option key={country.key} value={country.key}>
                      {translate(country)}
                    </option>
                  ),
              )}
            </optgroup>
          ))}
        </Select>
      )}
    </Filter>
  )
}

export default function Filters({
  asAccordion = false,
  countryAtom = countryId,
  cityAtom = cityId,
  cuisineAtom = cuisineId,
  halalAtom = filterHalal,
  citySelector = cityData,
  cuisineSelector = cuisineData,
}) {
  const { locale } = useParams()

  const { filters } = useContext(Language)
  const id = useRecoilValue(countryAtom)
  const [showCuisineFilter, setShowCuisineFilter] = useState(true)
  const [showJapanInDropdown] = useState(true)
  const [regionType, setRegionType] = useState('city')
  const [regionDefault, setRegionDefault] = useState('cityDefault')
  const merchants = useRecoilValue(merchantsData(locale))
  const [filterHalalMerchants, setFilterHalalMerchants] =
    useRecoilState(halalAtom)
  const areThereHalalMerchants = hasHalalMerchants(merchants)

  function enableHalalFilter() {
    const newValue = !filterHalalMerchants
    setFilterHalalMerchants(newValue)
    window.tuiTrack.trackEvent('click', `filterSelect:halal:${newValue}`)
  }

  function hasHalalMerchants(merchants) {
    return merchants?.some((merchant) => merchant.businessData.isHalal)
  }

  const twoFilters =
    locale === 'fr-fr' || locale === 'fr-ca' || locale === 'en-ca'

  const halalLabel = twoFilters
    ? locale === 'en-ca'
      ? 'Halal Only'
      : 'Repas halal uniquement'
    : 'Halal Only'

  // Rendered inline with the Cuisine filter's label on desktop. Mobile keeps
  // the standalone row below (an accordion header isn't a natural place for
  // an inline checkbox).
  const halalCheckbox = areThereHalalMerchants && (
    <InlineCheckbox>
      <input
        type="checkbox"
        id="halal"
        name="halal"
        checked={filterHalalMerchants}
        onChange={enableHalalFilter}
      />
      {halalLabel}
    </InlineCheckbox>
  )

  useEffect(() => {
    setShowCuisineFilter(id !== 'JP')
    setRegionType(id === 'AU' ? 'state' : 'city')
    setRegionDefault(id === 'AU' ? 'stateDefault' : 'cityDefault')
  }, [id])

  return (
    <>
      <Container id="filters">
        <Suspense fallback={<FiltersSuspense filters={filters} />}>
          <FilterCountry
            label={filters.country}
            showJapan={showJapanInDropdown}
            asAccordion={asAccordion}
            countryAtom={countryAtom}
            cityAtom={cityAtom}
            cuisineAtom={cuisineAtom}
          />
          <FilterStrings
            name="city"
            atom={cityAtom}
            selector={citySelector}
            label={filters[regionType]}
            all={filters[regionDefault]}
            asAccordion={asAccordion}
          />
          {showCuisineFilter && (
            <FilterStrings
              name="cuisine"
              atom={cuisineAtom}
              selector={cuisineSelector}
              label={filters.cuisine}
              all={filters.cuisineDefault}
              asAccordion={asAccordion}
              labelExtra={!asAccordion ? halalCheckbox : null}
            />
          )}
          {/* On mobile (accordion) the checkbox can't dock inline with the
          Cuisine label — a header row — so it gets its own row just below
          it instead. */}
          {asAccordion && showCuisineFilter && halalCheckbox && (
            <AccordionCheckboxRow>{halalCheckbox}</AccordionCheckboxRow>
          )}
          {/* Cuisine filter isn't always shown (e.g. Japan) — fall back to a
          standalone row so the toggle is never lost. */}
          {!showCuisineFilter && halalCheckbox && <Filter>{halalCheckbox}</Filter>}
        </Suspense>
      </Container>
    </>
  )
}
